import { useLazyQuery, useMutation } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Button, Form, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { customSelectDropDown } from '../../../components/styled/customSelect';
import { AppContext } from '../../../context';
import { FETCHING_ALL_USERS_WITH_PAGINATION } from '../gql';
import { CREATE_BILLING_CREDIT, UPDATE_BILLING_CREDIT } from './gql';

const BILLING_TYPE_OPTIONS = [
  { label: 'Temp', value: 'temp' },
  { label: 'Direct Hire', value: 'directHire' },
];

const BILLING_TYPE_KEY_MAP: Record<string, string> = {
  TEMP: 'temp',
  DIRECT_HIRE: 'directHire',
};

const BILLING_TYPE_VALUE_TO_KEY: Record<string, string> = {
  temp: 'TEMP',
  directHire: 'DIRECT_HIRE',
};

const normalizeBillingType = (value?: string) =>
  value ? (BILLING_TYPE_KEY_MAP[value] ?? value) : 'temp';

const MONTH_OPTIONS = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

const formatCurrency = (val: number | string) => {
  const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^0-9.]/g, ''));
  if (isNaN(num) || val === '' || val === undefined) return '';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

interface BillingCreditModalProps {
  isOpen: boolean;
  toggle: () => void;
  editRecord?: any;
  onSuccess: () => void;
}

const BillingCreditModal = ({ isOpen, toggle, editRecord, onSuccess }: BillingCreditModalProps) => {
  const { theme } = useContext(AppContext);
  const isEdit = !!editRecord;

  const now = new Date();
  const defaultValues = {
    userId: editRecord?.user?.id || '',
    billingType: normalizeBillingType(editRecord?.billingType),
    billingCreditAmount: editRecord?.billingCreditAmount ?? '',
    currentMonth: editRecord?.currentMonth || now.getMonth() + 1,
    currentYear: editRecord?.currentYear || now.getFullYear(),
  };

  const { control, handleSubmit, reset, register, formState: { errors } } = useForm({ defaultValues });

  const [amountDisplay, setAmountDisplay] = useState('');

  const [fetchUsers, { data: usersData }] = useLazyQuery(FETCHING_ALL_USERS_WITH_PAGINATION);
  const [createBillingCredit, { loading: creating }] = useMutation(CREATE_BILLING_CREDIT);
  const [updateBillingCredit, { loading: updating }] = useMutation(UPDATE_BILLING_CREDIT);

  useEffect(() => {
    if (isOpen) {
      fetchUsers({ variables: { userInput: { userStatus: 'ACTIVE', limit: 200, page: 1, includeTestUsers: true } } });
      reset(defaultValues);
      setAmountDisplay(editRecord?.billingCreditAmount != null ? formatCurrency(editRecord.billingCreditAmount) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const userOptions = (usersData?.fetchAllUsers?.users || []).map((u: any) => ({
    label: u.fullName,
    value: u.id,
  }));

  const onSubmit = async (data: any) => {
    try {
      const input = {
        userId: data.userId,
        billingType: BILLING_TYPE_VALUE_TO_KEY[data.billingType] ?? data.billingType,
        billingCreditAmount: parseFloat(data.billingCreditAmount),
        currentMonth: parseInt(data.currentMonth),
        currentYear: parseInt(data.currentYear),
      };

      if (isEdit) {
        await updateBillingCredit({ variables: { input: { id: editRecord.id, ...input } } });
        toast.success('Billing credit updated successfully.');
      } else {
        await createBillingCredit({ variables: { input } });
        toast.success('Billing credit created successfully.');
      }
      onSuccess();
      toggle();
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{isEdit ? 'Edit Billing Credit' : 'Add Billing Credit'}</ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <FormGroup>
            <Label>User <span className="text-danger">*</span></Label>
            <Controller
              control={control}
              name="userId"
              rules={{ required: 'User is required' }}
              render={({ field: { onChange, value } }) => (
                <Select
                  options={userOptions}
                  value={userOptions.find((o: any) => o.value === value) || null}
                  onChange={(opt: any) => onChange(opt?.value)}
                  placeholder="Select user..."
                  isDisabled={isEdit}
                  styles={customSelectDropDown(theme)}
                />
              )}
            />
            {errors.userId && <small className="text-danger">{errors.userId.message as string}</small>}
          </FormGroup>

          <FormGroup>
            <Label>Billing Type <span className="text-danger">*</span></Label>
            <Controller
              control={control}
              name="billingType"
              rules={{ required: 'Billing type is required' }}
              render={({ field: { onChange, value } }) => (
                <Select
                  options={BILLING_TYPE_OPTIONS}
                  value={BILLING_TYPE_OPTIONS.find((o) => o.value === value) || null}
                  onChange={(opt: any) => onChange(opt?.value)}
                  styles={customSelectDropDown(theme)}
                />
              )}
            />
            {errors.billingType && <small className="text-danger">{errors.billingType.message as string}</small>}
          </FormGroup>

          <FormGroup>
            <Label>Month <span className="text-danger">*</span></Label>
            <Controller
              control={control}
              name="currentMonth"
              rules={{ required: 'Month is required' }}
              render={({ field: { onChange, value } }) => (
                <Select
                  options={MONTH_OPTIONS}
                  value={MONTH_OPTIONS.find((o) => o.value === Number(value)) || null}
                  onChange={(opt: any) => onChange(opt?.value)}
                  styles={customSelectDropDown(theme)}
                />
              )}
            />
            {errors.currentMonth && <small className="text-danger">{errors.currentMonth.message as string}</small>}
          </FormGroup>

          <FormGroup>
            <Label>Year <span className="text-danger">*</span></Label>
            <input
              type="number"
              className="form-control"
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              {...register('currentYear', { required: 'Year is required', min: 2000, max: 2100 })}
            />
            {errors.currentYear && <small className="text-danger">{errors.currentYear.message as string}</small>}
          </FormGroup>

          <FormGroup>
            <Label>Amount <span className="text-danger">*</span></Label>
            <Controller
              control={control}
              name="billingCreditAmount"
              rules={{ required: 'Amount is required', min: { value: 0, message: 'Amount must be 0 or more' } }}
              render={({ field: { onChange, ref } }) => (
                <input
                  ref={ref}
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  value={amountDisplay}
                  onFocus={() => {
                    setAmountDisplay(amountDisplay.replace(/[^0-9.]/g, ''));
                  }}
                  onBlur={() => {
                    const num = parseFloat(amountDisplay.replace(/[^0-9.]/g, ''));
                    if (!isNaN(num)) {
                      setAmountDisplay(formatCurrency(num));
                      onChange(num);
                    } else {
                      setAmountDisplay('');
                      onChange('');
                    }
                  }}
                  onChange={(e) => {
                    setAmountDisplay(e.target.value);
                    const num = parseFloat(e.target.value.replace(/[^0-9.]/g, ''));
                    onChange(isNaN(num) ? '' : num);
                  }}
                />
              )}
            />
            {errors.billingCreditAmount && <small className="text-danger">{errors.billingCreditAmount.message as string}</small>}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} type="button">Cancel</Button>
          <Button color="primary" type="submit" disabled={creating || updating}>
            {creating || updating ? 'Saving...' : isEdit ? 'Update' : 'Add'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default BillingCreditModal;
